<template>
  <div class="user-bridge-association-container">
    <div class="page-header">
      <h2 class="page-title">用户桥梁关联管理</h2>
    </div>

    <el-card class="main-card">
      <div class="filter-section">
        <el-form :inline="true" :model="filterForm">
          <el-form-item label="用户名">
            <el-select 
              v-model="filterForm.userId" 
              filterable 
              placeholder="请选择用户"
              clearable>
              <el-option
                v-for="user in userOptions"
                :key="user.id"
                :label="user.username + (user.realName ? ` (${user.realName})` : '')"
                :value="user.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="桥梁">
            <el-select 
              v-model="filterForm.bridgeId" 
              filterable 
              placeholder="请选择桥梁"
              clearable>
              <el-option
                v-for="bridge in bridgeOptions"
                :key="bridge.id"
                :label="bridge.bridgeName"
                :value="bridge.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="角色类型">
            <el-select 
              v-model="filterForm.roleType" 
              placeholder="请选择角色类型"
              clearable>
              <el-option label="桥梁管理员" :value="1"></el-option>
              <el-option label="值班室用户" :value="2"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
            <el-button type="success" @click="handleAddAssociation">新增关联</el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-table
        v-loading="loading"
        :data="associationList"
        border
        style="width: 100%">
        <el-table-column
          prop="id"
          label="ID"
          width="80">
        </el-table-column>
        <el-table-column
          prop="username"
          label="用户名"
          width="120">
        </el-table-column>
        <el-table-column
          prop="realName"
          label="名称"
          width="120">
        </el-table-column>
        <el-table-column
          prop="bridgeName"
          label="桥梁名称"
          width="150">
        </el-table-column>
        <el-table-column
          prop="bridgeCode"
          label="桥梁编码"
          width="120">
        </el-table-column>
        <el-table-column
          prop="roleType"
          label="角色类型"
          width="120">
          <template slot-scope="scope">
            <el-tag :type="scope.row.roleType === 1 ? 'primary' : 'success'">
              {{ scope.row.roleType === 1 ? '桥梁管理员' : '值班室用户' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="createTime"
          label="创建时间"
          width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="150">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 关联编辑对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      :visible.sync="dialogVisible"
      width="550px">
      <el-form 
        ref="associationForm" 
        :model="associationForm" 
        :rules="associationRules" 
        label-width="100px">
        <el-form-item label="用户" prop="userId">
          <el-select 
            v-model="associationForm.userId" 
            filterable 
            placeholder="请选择用户"
            :disabled="editMode">
            <el-option
              v-for="user in userOptions"
              :key="user.id"
              :label="user.username + (user.realName ? ` (${user.realName})` : '')"
              :value="user.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="桥梁" prop="bridgeId">
          <el-select 
            v-model="associationForm.bridgeId" 
            filterable 
            placeholder="请选择桥梁"
            :disabled="editMode">
            <el-option
              v-for="bridge in bridgeOptions"
              :key="bridge.id"
              :label="bridge.bridgeName"
              :value="bridge.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="角色类型" prop="roleType">
          <el-radio-group v-model="associationForm.roleType">
            <el-radio :label="1">桥梁管理员</el-radio>
            <el-radio :label="2">值班室用户</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 批量关联对话框 -->
    <el-dialog 
      title="批量关联桥梁" 
      :visible.sync="batchDialogVisible"
      width="650px">
      <el-form 
        ref="batchForm" 
        :model="batchForm" 
        :rules="batchRules" 
        label-width="100px">
        <el-form-item label="关联类型">
          <el-radio-group v-model="batchAssignType">
            <el-radio :label="'user'">一个用户关联多个桥梁</el-radio>
            <el-radio :label="'bridge'">一个桥梁关联多个用户</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <template v-if="batchAssignType === 'user'">
          <el-form-item label="用户" prop="userId">
            <el-select 
              v-model="batchForm.userId" 
              filterable 
              placeholder="请选择用户">
              <el-option
                v-for="user in userOptions"
                :key="user.id"
                :label="user.username + (user.realName ? ` (${user.realName})` : '')"
                :value="user.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="桥梁" prop="bridgeIds">
            <el-transfer
              v-model="batchForm.bridgeIds"
              :data="bridgeOptions"
              :titles="['可选桥梁', '已选桥梁']"
              :props="{
                key: 'id',
                label: 'name'
              }"
              filterable
              filter-placeholder="请输入桥梁名称">
            </el-transfer>
          </el-form-item>
        </template>
        
        <template v-else>
          <el-form-item label="桥梁" prop="bridgeId">
            <el-select 
              v-model="batchForm.bridgeId" 
              filterable 
              placeholder="请选择桥梁">
              <el-option
                v-for="bridge in bridgeOptions"
                :key="bridge.id"
                :label="bridge.bridgeName"
                :value="bridge.id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="用户" prop="userIds">
            <el-transfer
              v-model="batchForm.userIds"
              :data="userOptions"
              :titles="['可选用户', '已选用户']"
              :props="{
                key: 'id',
                label: (data) => data.username + (data.realName ? ` (${data.realName})` : '')
              }"
              filterable
              filter-placeholder="请输入用户名">
            </el-transfer>
          </el-form-item>
        </template>
        
        <el-form-item label="角色类型" prop="roleType">
          <el-radio-group v-model="batchForm.roleType">
            <el-radio :label="1">桥梁管理员</el-radio>
            <el-radio :label="2">值班室用户</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchForm" :loading="submitting">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getUserList
} from '@/api/auth'
import {
  getAllBridges
} from '@/api/bridge'
import {
  getUserBridgeAssociations,
  createUserBridgeAssociation,
  updateUserBridgeAssociation,
  deleteUserBridgeAssociation,
  batchAssignBridges,
  batchAssignUsers
} from '@/api/user-bridge'

export default {
  name: 'UserBridgeAssociation',
  data() {
    return {
      loading: false,
      submitting: false,
      // 列表数据
      associationList: [],
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      // 过滤表单
      filterForm: {
        userId: '',
        bridgeId: '',
        roleType: ''
      },
      // 选项数据
      userOptions: [],
      bridgeOptions: [],
      // 单个关联表单
      dialogVisible: false,
      dialogTitle: '新增关联',
      editMode: false,
      associationForm: {
        id: null,
        userId: '',
        bridgeId: '',
        roleType: 1
      },
      associationRules: {
        userId: [
          { required: true, message: '请选择用户', trigger: 'change' }
        ],
        bridgeId: [
          { required: true, message: '请选择桥梁', trigger: 'change' }
        ],
        roleType: [
          { required: true, message: '请选择角色类型', trigger: 'change' }
        ]
      },
      // 批量关联表单
      batchDialogVisible: false,
      batchAssignType: 'user',
      batchForm: {
        userId: '',
        bridgeId: '',
        userIds: [],
        bridgeIds: [],
        roleType: 1
      },
      batchRules: {
        userId: [
          { required: true, message: '请选择用户', trigger: 'change' }
        ],
        bridgeId: [
          { required: true, message: '请选择桥梁', trigger: 'change' }
        ],
        roleType: [
          { required: true, message: '请选择角色类型', trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.fetchUserOptions()
    this.fetchBridgeOptions()
    this.fetchAssociationList()
  },
  methods: {
    // 获取用户选项
    async fetchUserOptions() {
      try {
        // 使用适合PostgreSQL的分页参数
        const params = {
          page: 0,
          size: 1000, // 获取尽可能多的用户
          username: '',
          realName: ''
        }
        const res = await getUserList(params)
        if (res.code === 200) {
          this.userOptions = res.data.content || res.data.list || []
        } else {
          this.$message.error(res.message || '获取用户列表失败')
        }
      } catch (error) {
        console.error('获取用户列表出错:', error)
        this.$message.error('获取用户列表失败')
      }
    },
    
    // 获取桥梁选项
    async fetchBridgeOptions() {
      try {
        const res = await getAllBridges()
        if (res.code === 200) {
          // 倒序排列桥梁列表
          this.bridgeOptions = (res.data || []).reverse()
        } else {
          this.$message.error(res.message || '获取桥梁列表失败')
        }
      } catch (error) {
        console.error('获取桥梁列表出错:', error)
        this.$message.error('获取桥梁列表失败')
      }
    },
    
    // 获取关联列表
    async fetchAssociationList() {
      this.loading = true
      try {
        const { pageNum, pageSize } = this.pagination
        const { userId, bridgeId, roleType } = this.filterForm
        
        const params = {
          pageNum,
          pageSize,
          userId,
          bridgeId,
          roleType
        }
        
        const res = await getUserBridgeAssociations(params)
        if (res.code === 200) {
          this.associationList = res.data.list || []
          this.pagination.total = res.data.total
        } else {
          this.$message.error(res.message || '获取关联列表失败')
        }
      } catch (error) {
        console.error('获取关联列表出错:', error)
        this.$message.error('获取关联列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchAssociationList()
    },
    
    // 重置过滤条件
    resetFilter() {
      this.filterForm = {
        userId: '',
        bridgeId: '',
        roleType: ''
      }
      this.handleSearch()
    },
    
    // 添加关联
    handleAddAssociation() {
      this.dialogTitle = '新增关联'
      this.editMode = false
      this.associationForm = {
        id: null,
        userId: '',
        bridgeId: '',
        roleType: 1
      }
      this.dialogVisible = true
      
      this.$nextTick(() => {
        this.$refs.associationForm && this.$refs.associationForm.clearValidate()
      })
    },
    
    // 编辑关联
    handleEdit(row) {
      this.dialogTitle = '编辑关联'
      this.editMode = true
      this.associationForm = {
        id: row.id,
        userId: row.userId,
        bridgeId: row.bridgeId,
        roleType: row.roleType
      }
      this.dialogVisible = true
      
      this.$nextTick(() => {
        this.$refs.associationForm && this.$refs.associationForm.clearValidate()
      })
    },
    
    // 删除关联
    async handleDelete(row) {
      try {
        await this.$confirm(`确定要删除此关联关系吗?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await deleteUserBridgeAssociation(row.id)
        
        if (res.code === 200) {
          this.$message.success('删除成功')
          this.fetchAssociationList()
        } else {
          this.$message.error(res.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除关联出错:', error)
          this.$message.error('删除失败')
        }
      }
    },
    
    // 提交表单
    submitForm() {
      this.$refs.associationForm.validate(async valid => {
        if (valid) {
          this.submitting = true
          try {
            let res
            
            if (this.editMode) {
              // 更新关联
              res = await updateUserBridgeAssociation(this.associationForm.id, this.associationForm)
            } else {
              // 创建关联
              res = await createUserBridgeAssociation(this.associationForm)
            }
            
            if (res.code === 200) {
              this.$message.success(this.editMode ? '更新成功' : '创建成功')
              this.dialogVisible = false
              this.fetchAssociationList()
            } else {
              this.$message.error(res.message || (this.editMode ? '更新失败' : '创建失败'))
            }
          } catch (error) {
            console.error('提交表单出错:', error)
            this.$message.error(this.editMode ? '更新失败' : '创建失败')
          } finally {
            this.submitting = false
          }
        } else {
          return false
        }
      })
    },
    
    // 打开批量关联对话框
    handleBatchAssign() {
      this.batchAssignType = 'user'
      this.batchForm = {
        userId: '',
        bridgeId: '',
        userIds: [],
        bridgeIds: [],
        roleType: 1
      }
      this.batchDialogVisible = true
      
      this.$nextTick(() => {
        this.$refs.batchForm && this.$refs.batchForm.clearValidate()
      })
    },
    
    // 提交批量关联表单
    submitBatchForm() {
      // 根据批量分配类型选择需要验证的字段
      if (this.batchAssignType === 'user') {
        this.$refs.batchForm.clearValidate()
        this.$refs.batchForm.validateField(['userId', 'bridgeIds', 'roleType'])
      } else {
        this.$refs.batchForm.clearValidate()
        this.$refs.batchForm.validateField(['bridgeId', 'userIds', 'roleType'])
      }

      this.$refs.batchForm.validate(async valid => {
        if (valid) {
          this.submitting = true
          try {
            let res
            
            if (this.batchAssignType === 'user') {
              // 一个用户关联多个桥梁
              const params = {
                userId: this.batchForm.userId,
                bridgeIds: this.batchForm.bridgeIds,
                roleType: this.batchForm.roleType
              }
              res = await batchAssignBridges(params)
            } else {
              // 一个桥梁关联多个用户
              const params = {
                bridgeId: this.batchForm.bridgeId,
                userIds: this.batchForm.userIds,
                roleType: this.batchForm.roleType
              }
              res = await batchAssignUsers(params)
            }
            
            if (res.code === 200) {
              this.$message.success('批量关联成功')
              this.batchDialogVisible = false
              this.fetchAssociationList()
            } else {
              this.$message.error(res.message || '批量关联失败')
            }
          } catch (error) {
            console.error('批量关联出错:', error)
            this.$message.error('批量关联失败')
          } finally {
            this.submitting = false
          }
        } else {
          return false
        }
      })
    },
    
    // 分页相关
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.fetchAssociationList()
    },
    
    handleCurrentChange(val) {
      this.pagination.pageNum = val
      this.fetchAssociationList()
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString()
    }
  }
}
</script>

<style scoped>
.user-bridge-association-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-title {
  font-size: 20px;
  color: #303133;
  margin: 0;
}

.main-card {
  margin-bottom: 20px;
}

.filter-section {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style> 